import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, MapPinned } from 'lucide-react';
import { adminApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { OutOfZoneStats } from './OutOfZoneStats';

const EMPTY = { last_name: '', first_name: '', address: '', postal_code: '', city: '', phone: '', note: '', sector_id: '', is_active: true };
const FIELDS = [
  ['last_name', 'Nom *'], ['first_name', 'Prénom *'], ['address', 'Adresse (numéro et rue) *'],
  ['postal_code', 'Code postal *'], ['city', 'Ville *'], ['phone', 'Téléphone'],
];

const ExceptionForm = ({ form, setForm, sectors }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {FIELDS.map(([key, label]) => (
      <div key={key} className={key === 'address' ? 'sm:col-span-2' : ''}>
        <Label>{label}</Label>
        <Input value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`addr-exc-input-${key}`} />
      </div>
    ))}
    <div className="sm:col-span-2">
      <Label>Secteur qui assure la prestation (cuisiniers et créneaux) *</Label>
      <Select value={form.sector_id} onValueChange={(v) => setForm({ ...form, sector_id: v })}>
        <SelectTrigger data-testid="addr-exc-sector-select"><SelectValue placeholder="Choisir un secteur" /></SelectTrigger>
        <SelectContent>
          {sectors.map((s) => <SelectItem key={s.sector_id} value={s.sector_id} data-testid={`addr-exc-sector-${s.sector_id}`}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-xs text-[#5C6A61] mt-1">Aucun supplément déplacement n'est appliqué aux adresses en exception.</p>
    </div>
    <div className="sm:col-span-2">
      <Label>Note</Label>
      <Textarea value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="addr-exc-input-note" />
    </div>
    <div className="flex items-center gap-3">
      <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="addr-exc-input-active" />
      <span className="text-sm">{form.is_active ? 'Active' : 'Inactive'}</span>
    </div>
  </div>
);

const ExceptionRow = ({ it, onEdit, onToggle, onDelete }) => (
  <div className="card-default flex flex-col md:flex-row md:items-start justify-between gap-4" data-testid={`addr-exc-row-${it.exception_id}`}>
    <div className="space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-semibold text-[#2A312C]">{it.first_name} {it.last_name}</p>
        <Badge className={it.is_active ? 'bg-[#A5BBA8] text-white' : 'bg-gray-200 text-gray-700'}>{it.is_active ? 'Active' : 'Inactive'}</Badge>
        {it.in_served_zone && <Badge className="bg-[#E8B25C] text-white">Adresse déjà en zone desservie</Badge>}
        {!it.sector_active && <Badge className="bg-[#C86B51] text-white">Secteur inactif</Badge>}
      </div>
      <p className="text-sm text-[#5C6A61]">{it.address}, {it.postal_code} {it.city}{it.phone ? ` · ${it.phone}` : ''}</p>
      <p className="text-sm text-[#5C6A61]">Secteur : {it.sector_name || '—'}</p>
      {it.note && <p className="text-sm italic text-[#5C6A61]">{it.note}</p>}
      <p className="text-xs text-[#5C6A61]" data-testid={`addr-exc-match-${it.exception_id}`}>
        {it.matched_accounts?.length
          ? `Compte client reconnu : ${it.matched_accounts.map((u) => `${u.name} (${u.email})`).join(', ')}`
          : "Aucun compte client ne correspond encore (nom + adresse) — le client pourra réserver dès qu'il aura créé son compte avec cette adresse."}
      </p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Switch checked={it.is_active} onCheckedChange={() => onToggle(it)} data-testid={`addr-exc-toggle-${it.exception_id}`} />
      <Button variant="outline" size="sm" onClick={() => onEdit(it)} data-testid={`addr-exc-edit-${it.exception_id}`}><Pencil className="w-4 h-4" /></Button>
      <Button variant="outline" size="sm" onClick={() => onDelete(it)} className="text-[#C86B51]" data-testid={`addr-exc-delete-${it.exception_id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

const toPayload = (f) => ({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]])) });

const fromAttempt = (a) => {
  const [first, ...rest] = String(a.name || '').trim().split(/\s+/);
  return { ...EMPTY, first_name: first || '', last_name: rest.join(' '), address: a.address || '', postal_code: a.postal_code || '', city: a.city || '', phone: a.phone || '', note: `Demande du ${new Date(a.last_attempt_at).toLocaleDateString('fr-FR')}${a.email ? ` — ${a.email}` : ''}`, attempt_id: a.attempt_id };
};

const SOURCE_LABELS = { ponctuelle: 'Réservation ponctuelle', recurrente: 'Créneau récurrent', verification: 'Vérification de zone (tunnel de réservation)' };

const AttemptRow = ({ a, onAuthorize, onDismiss }) => (
  <div className="card-default flex flex-col md:flex-row md:items-start justify-between gap-4" data-testid={`ooz-attempt-${a.attempt_id}`}>
    <div className="space-y-1">
      <p className="font-semibold text-[#2A312C]">{a.name || 'Client sans nom'}</p>
      <p className="text-sm text-[#5C6A61]">{a.address || 'Adresse non renseignée'}, {a.postal_code} {a.city}</p>
      <p className="text-sm text-[#5C6A61]">{[a.email, a.phone].filter(Boolean).join(' · ') || 'Aucune coordonnée'}</p>
      <p className="text-xs text-[#5C6A61]">
        Dernière tentative : {new Date(a.last_attempt_at).toLocaleString('fr-FR')} · {SOURCE_LABELS[a.last_source] || a.last_source} · {a.attempts} tentative{a.attempts > 1 ? 's' : ''}
      </p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Button onClick={() => onAuthorize(a)} className="bg-[#A5BBA8] hover:bg-[#8fa893] text-white" data-testid={`ooz-authorize-${a.attempt_id}`}>Autoriser cette adresse</Button>
      <Button variant="outline" onClick={() => onDismiss(a)} data-testid={`ooz-dismiss-${a.attempt_id}`}>Ignorer</Button>
    </div>
  </div>
);

export default function AddressExceptionsManagement() {
  const [items, setItems] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => {
    setRefreshKey((k) => k + 1);
    adminApi.getOutOfZoneAttempts().then((r) => setAttempts(r.data)).catch(() => {});
    return adminApi.getAddressExceptions().then((r) => setItems(r.data)).catch(() => toast.error('Chargement impossible'));
  };

  const dismiss = async (a) => {
    try {
      await adminApi.dismissOutOfZoneAttempt(a.attempt_id);
      load();
    } catch (e) {
      toast.error('Action impossible');
    }
  };
  useEffect(() => {
    load();
    adminApi.getSectors().then((r) => setSectors(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (form.exception_id) await adminApi.updateAddressException(form.exception_id, toPayload(form));
      else await adminApi.createAddressException(toPayload(form));
      toast.success('Exception enregistrée');
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (it) => {
    try {
      await adminApi.updateAddressException(it.exception_id, toPayload({ ...it, is_active: !it.is_active }));
      load();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer l'exception de ${it.first_name} ${it.last_name} ?`)) return;
    try {
      await adminApi.deleteAddressException(it.exception_id);
      toast.success('Exception supprimée');
      load();
    } catch (e) {
      toast.error('Suppression impossible');
    }
  };

  if (!items) return <Loader2 className="w-6 h-6 animate-spin text-[#A5BBA8]" />;

  return (
    <div className="space-y-6" data-testid="address-exceptions-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <MapPinned className="w-6 h-6 text-[#C86B51] mt-1" />
          <div>
            <h1 className="text-2xl font-semibold text-[#2A312C]">Adresses exceptionnellement autorisées</h1>
            <p className="text-sm text-[#5C6A61]">Clients hors zone autorisés à réserver. Les zones desservies ne changent pas. Le nom, le prénom et l'adresse du compte client doivent correspondre (majuscules, accents et espaces ignorés).</p>
          </div>
        </div>
        <Button onClick={() => setForm({ ...EMPTY })} className="bg-[#2A312C] hover:bg-[#3a443d]" data-testid="addr-exc-add-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter une adresse
        </Button>
      </div>

      <OutOfZoneStats refreshKey={refreshKey} />

      <div className="space-y-3" data-testid="ooz-attempts-section">
        <h2 className="text-lg font-semibold text-[#2A312C]">Demandes hors zone sans exception ({attempts.length})</h2>
        {attempts.length === 0
          ? <p className="text-sm text-[#5C6A61]" data-testid="ooz-attempts-empty">Aucune tentative de réservation hors zone en attente.</p>
          : attempts.map((a) => <AttemptRow key={a.attempt_id} a={a} onAuthorize={(x) => setForm(fromAttempt(x))} onDismiss={dismiss} />)}
      </div>

      <h2 className="text-lg font-semibold text-[#2A312C]">Exceptions enregistrées ({items.length})</h2>
      {items.length === 0
        ? <p className="text-sm text-[#5C6A61]" data-testid="addr-exc-empty">Aucune adresse en exception pour le moment.</p>
        : items.map((it) => <ExceptionRow key={it.exception_id} it={it} onEdit={(x) => setForm({ ...x })} onToggle={toggle} onDelete={remove} />)}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl" data-testid="addr-exc-dialog">
          <DialogHeader><DialogTitle>{form?.exception_id ? "Modifier l'exception" : 'Nouvelle adresse autorisée'}</DialogTitle></DialogHeader>
          {form && <ExceptionForm form={form} setForm={setForm} sectors={sectors} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} data-testid="addr-exc-cancel-btn">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#2A312C] hover:bg-[#3a443d]" data-testid="addr-exc-save-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
